(function (scope) {
  'use strict';
  if (scope.TikiLandAnnouncements) { scope.TikiLandAnnouncements.refresh(); return; }
  const doc = scope.document;
  const instances = new Map();
  const memory = new Map();
  const DAY = 86400000;
  let privacyLoadRequested = false;
  const isEditor = () => Boolean(scope.Shopify && scope.Shopify.designMode);
  function permission() {
    try {
      const privacy = scope.Shopify && scope.Shopify.customerPrivacy;
      return privacy && typeof privacy.preferencesProcessingAllowed === 'function' ? privacy.preferencesProcessingAllowed() === true : null;
    } catch (_) { return null; }
  }
  function storageKey(root) {
    const theme = scope.Shopify && scope.Shopify.theme;
    if (!theme || !theme.id || !theme.role) return null;
    return 'tl_announcement_dismiss_v1_' + String(theme.id) + '_' + (theme.role === 'main' ? 'live' : 'draft') + '_' + root.dataset.tlAnnouncementSection + '_' + root.dataset.tlAnnouncement;
  }
  function duration(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(1, Math.min(90, Math.trunc(n))) : 7;
  }
  function canonicalCopy(root) {
    const copy = root.querySelector('[data-tl-announcement-copy]');
    if (!copy) return '';
    const clone = copy.cloneNode(true);
    clone.querySelectorAll('br').forEach(el => el.replaceWith(doc.createTextNode(' ')));
    clone.querySelectorAll('p,li,div,h1,h2,h3,h4,h5,h6').forEach(el => el.appendChild(doc.createTextNode(' ')));
    const text = clone.textContent.replace(/\s+/g, ' ').trim();
    const links = Array.from(copy.querySelectorAll('a[href]'));
    const outerLink = copy.closest('a[href]');
    if (outerLink) links.unshift(outerLink);
    return JSON.stringify([text, links.map(link => {
      try { return new scope.URL(link.getAttribute('href'), doc.baseURI).href; }
      catch (_) { return String(link.getAttribute('href') || '').trim(); }
    })]);
  }
  async function signature(text) {
    try {
      const result = await scope.crypto.subtle.digest('SHA-256', new scope.TextEncoder().encode(text));
      return Array.from(new Uint8Array(result), b => b.toString(16).padStart(2, '0')).join('');
    } catch (_) {
      // Older contexts still work without storing any message text.
      let a = 2166136261, b = 5381;
      for (let i = 0; i < text.length; i++) { a = Math.imul(a ^ text.charCodeAt(i), 16777619); b = Math.imul(b, 33) ^ text.charCodeAt(i); }
      return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
    }
  }
  function valid(record, instance) {
    if (!record || record.v !== 1 || record.mode !== instance.mode) return false;
    if (record.mode === 'message') return record.signature === instance.signature;
    return Number.isFinite(record.until) && record.until > Date.now() && record.until <= Date.now() + 91 * DAY;
  }
  function forget(key) { if (key) { try { scope.localStorage.removeItem(key); } catch (_) {} } }
  function stored(instance) {
    if (isEditor() || permission() !== true || !instance.key) return null;
    try {
      const raw = scope.localStorage.getItem(instance.key);
      if (!raw) return null;
      const record = JSON.parse(raw);
      if (valid(record, instance)) return record;
      forget(instance.key);
    } catch (_) { /* Storage may be blocked or malformed. */ }
    return null;
  }
  function apply(instance) {
    if (!instance.ready || !instance.root.isConnected) return;
    const allowed = permission();
    if (allowed === false && instance.lastPermission !== false && !isEditor()) forget(instance.key);
    instance.lastPermission = allowed;
    let choice = memory.get(instance.memoryKey);
    if (choice && !valid(choice, instance)) { memory.delete(instance.memoryKey); choice = null; }
    const saved = stored(instance);
    instance.root.hidden = Boolean(choice || saved);
    instance.root.dataset.tlAnnouncementStorage = !isEditor() && allowed === true && instance.key ? 'browser' : 'page';
    instance.button.hidden = false;
  }
  function moveFocus(root) {
    if (!root.contains(doc.activeElement)) return;
    const candidates = doc.querySelectorAll('[data-tl-announcement]:not([hidden]) button, .site-header a, .site-header button');
    for (const candidate of candidates) {
      const rect = candidate.getBoundingClientRect();
      if (rect.width && rect.height && !candidate.hidden && scope.getComputedStyle(candidate).visibility !== 'hidden') {
        candidate.focus({ preventScroll: true }); return;
      }
    }
    const main = doc.querySelector('main');
    if (main) {
      const old = main.getAttribute('tabindex');
      main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true });
      if (old === null) main.removeAttribute('tabindex'); else main.setAttribute('tabindex', old);
    }
  }
  function dismiss(instance, event) {
    event.preventDefault(); event.stopPropagation();
    const record = instance.mode === 'message' ? { v: 1, mode: 'message', signature: instance.signature } : { v: 1, mode: 'days', until: Date.now() + instance.days * DAY };
    memory.set(instance.memoryKey, record);
    if (!isEditor() && permission() === true && instance.key) {
      try { scope.localStorage.setItem(instance.key, JSON.stringify(record)); } catch (_) { /* Page-local fallback. */ }
    }
    instance.root.hidden = true;
    moveFocus(instance.root);
    const status = doc.querySelector('[data-tl-announcement-status]');
    if (status) status.textContent = (instance.root.dataset.tlAnnouncement === 'first' ? 'First' : 'Second') + ' announcement dismissed.';
  }
  async function scan(resetEditor) {
    const pending = [];
    instances.forEach((instance, root) => {
      if (!root.isConnected || resetEditor) { instance.button.removeEventListener('click', instance.click); instances.delete(root); }
    });
    doc.querySelectorAll('[data-tl-announcement]').forEach(root => {
      if (instances.has(root)) return;
      const button = root.querySelector('[data-tl-announcement-close]');
      if (!button) return;
      button.hidden = true;
      if (resetEditor && isEditor()) root.hidden = false;
      const key = storageKey(root);
      const memoryKey = key || root.dataset.tlAnnouncementSection + '_' + root.dataset.tlAnnouncement;
      if (resetEditor && isEditor()) memory.delete(memoryKey);
      const instance = { root, button, key, memoryKey, mode: root.dataset.tlAnnouncementMode === 'days' ? 'days' : 'message', days: duration(root.dataset.tlAnnouncementDays), ready: false };
      instance.click = event => dismiss(instance, event);
      instances.set(root, instance);
      pending.push(signature(canonicalCopy(root)).then(value => {
        if (instances.get(root) !== instance) return;
        instance.signature = value; instance.ready = true;
        button.addEventListener('click', instance.click); apply(instance);
      }));
    });
    await Promise.all(pending);
    instances.forEach(apply);
  }
  function sectionChanged() { scan(isEditor()); }
  function refresh() {
    const shopify = scope.Shopify;
    if (!isEditor() && !privacyLoadRequested && shopify && !shopify.customerPrivacy && typeof shopify.loadFeatures === 'function') {
      privacyLoadRequested = true;
      try { shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { scan(false); }); }
      catch (_) { /* Unknown permission keeps page-only behavior. */ }
    }
    return scan(false);
  }
  doc.addEventListener('shopify:section:load', sectionChanged);
  doc.addEventListener('shopify:section:select', sectionChanged);
  doc.addEventListener('visitorConsentCollected', refresh);
  scope.addEventListener('storage', refresh);
  scope.addEventListener('pageshow', refresh);
  const timer = scope.setInterval(refresh, 1000);
  scope.TikiLandAnnouncements = { refresh, destroy: function () {
    scope.clearInterval(timer);
    doc.removeEventListener('shopify:section:load', sectionChanged);
    doc.removeEventListener('shopify:section:select', sectionChanged);
    doc.removeEventListener('visitorConsentCollected', refresh);
    scope.removeEventListener('storage', refresh); scope.removeEventListener('pageshow', refresh);
    instances.forEach(instance => { instance.button.removeEventListener('click', instance.click); instance.root.hidden = false; instance.button.hidden = true; });
    delete scope.TikiLandAnnouncements;
  } };
  refresh();
})(window);
